import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { Teacher, YoklaAssignment, ScannedData, QuestionData, BuraxilisGradingData, QuestionLock } from '../../types';
import { doc, updateDoc, Timestamp, getDoc, getDocFromServer, query, collection, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import StudentCard from '../StudentCard';
import TeacherSettingsModal from './TeacherSettingsModal';
import { getEnterKeyShortcutPreference } from '../../utils/teacherSettings';
import { getScannedDataByAssignment } from '../../services/yoklaService';
import { confirmApellyasiya, getNextPendingApellyasiya } from '../../services/apellyasiyaService';
import { getQuestionImageUrl } from '../../services/questionImageService';
import { Z_INDEX } from '../../styles/tokens';
import { useToast } from '../../contexts/ToastContext';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';
import { isQ26SpecialGrading } from '../../utils/examUtils';
import { acquireLock, releaseLock, extendLock, LockAcquisitionResult } from '../../services/lockService';

interface BuraxilisYoxlamaPageProps {
  teacher: Teacher;
}

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
  background: #1e3a8a;

  @media (max-width: 768px) {
    padding: 12px 0 16px 0;
  }
`;

const Header = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  position: relative;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
  min-width: 220px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
`;

const MetaTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
`;

const PoolBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
  color: white;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(168, 85, 247, 0.3);
`;

const LockWarningBanner = styled.div`
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border: 2px solid #f59e0b;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #92400e;
  font-weight: 500;
  font-size: 14px;

  .icon {
    font-size: 20px;
  }

  .text {
    flex: 1;
  }
`;

const HeaderMenuButton = styled.button`
  border: 1px solid #e2e8f0;
  background: #f9fafb;
  border-radius: 999px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
  transition: all 0.2s ease;

  &:hover {
    background: #eef2ff;
    border-color: #c7d2fe;
    color: #4f46e5;
    transform: translateY(-1px);
  }
`;

const HeaderMenu = styled.div`
  position: absolute;
  top: 56px;
  right: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.15);
  padding: 8px;
  min-width: 180px;
  z-index: ${Z_INDEX.dropdown};
`;

const HeaderMenuItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  padding: 8px 10px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #0f172a;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f1f5f9;
  }
`;

// NEW: Header actions container
const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

// QuestionBadge - smaller, non-clickable, displayed in HeaderLeft
const QuestionBadge = styled.div`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
`;

// NEW: Students button (replaces FAB)
const StudentsButton = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  .count {
    background: rgba(255, 255, 255, 0.2);
    padding: 2px 6px;
    border-radius: 999px;
    font-size: 11px;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FilterTab = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$active
    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    : '#f1f5f9'};
  color: ${props => props.$active ? 'white' : '#475569'};

  &:hover {
    background: ${props => props.$active
      ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
      : '#e2e8f0'};
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.2s ease;
  margin-bottom: 16px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          &:hover { box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3); }
        `;
      case 'secondary':
        return `
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          color: #475569;
          &:hover { box-shadow: 0 4px 12px rgba(100, 116, 139, 0.2); }
        `;
      default:
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          &:hover { box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3); }
        `;
    }
  }}

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
  }

  // Improved disabled state - more visually obvious
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
    background: #f3f4f6 !important;
    color: #9ca3af !important;
    border-color: #e5e7eb !important;
    position: relative;

    // Add visual indicator for disabled state
    &::after {
      content: '🔒';
      font-size: 12px;
      margin-left: 8px;
      opacity: 0.5;
    }
  }

  // Optional: Show tooltip on hover for disabled buttons with data-disabled-reason
  &[data-disabled-reason]:hover:not(:disabled) {
    position: relative;
  }
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    max-width: 100%;
    margin: 0;
  }
`;

const MainLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: 1fr 400px;
    gap: 18px;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ProgressBar = styled.div<{ $isCompleted?: boolean }>`
  background: ${props => props.$isCompleted
    ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
    : 'white'};
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 16px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
  border-left: ${props => props.$isCompleted ? '4px solid #10b981' : '4px solid #3b82f6'};
  transition: all 0.3s ease;
`;

const ProgressText = styled.div<{ $isCompleted?: boolean }>`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.$isCompleted ? '#166534' : '#1e293b'};
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;

  ${props => props.$isCompleted && `
    &::before {
      content: '✓';
      color: #10b981;
      font-size: 18px;
      font-weight: 700;
    }
  `}
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 12px;
  background: #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
`;

const ProgressBarInner = styled.div<{ width: number }>`
  height: 100%;
  background: linear-gradient(90deg, #10b981, #3b82f6);
  border-radius: 6px;
  width: ${props => props.width}%;
  transition: width 0.3s ease;
`;

const StudentGradingCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);

  @media (max-width: 768px) {
    padding: 12px 14px;
  }
`;

const ImageSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);

  @media (max-width: 768px) {
    padding: 12px 14px;
  }
`;

const GradingSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 18px 20px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);

  @media (max-width: 768px) {
    padding: 14px 16px;
  }
`;

const StudentInfo = styled.div`
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;

  @media (max-width: 768px) {
    margin-bottom: 10px;
    padding-bottom: 10px;
  }
`;

const StudentName = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const StudentDetails = styled.div`
  display: flex;
  gap: 16px;
  color: #64748b;
  font-size: 14px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 12px;
    font-size: 13px;
  }
`;

const QuestionImage = styled.div`
  margin-bottom: 24px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const ScoreInput = styled.div`
  margin-bottom: 24px;
`;

const ScoreLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
`;

const ScoreOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }
`;

const ScoreButton = styled.button<{ selected: boolean }>`
  padding: 16px 12px;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e2e8f0'};
  border-radius: 10px;
  background: ${props => props.selected ? '#dbeafe' : 'white'};
  color: ${props => props.selected ? '#1d4ed8' : '#64748b'};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    background: #f0f9ff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 12px 8px;
    font-size: 14px;
  }
`;

const NoteInput = styled.textarea`
  width: 100%;
  padding: 16px;
  font-size: 14px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.3s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;

  @media (max-width: 768px) {
    display: none;  /* Mobilde tamamen gizle - sticky footer kullanılıyor */
  }
`;

const EmptyState = styled.div`
  background: white;
  border-radius: 16px;
  padding: 60px 20px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const EmptyStateText = styled.p`
  font-size: 18px;
  color: #64748b;
  margin: 0;
`;

const CompletionMessage = styled.div`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-radius: 16px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  h2 {
    font-size: 28px;
    font-weight: 700;
    margin: 0 0 12px 0;
  }

  p {
    font-size: 16px;
    margin: 0 0 24px 0;
    opacity: 0.9;
  }
`;

const ImageModal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: ${Z_INDEX.modal};
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const ModalImageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;

  @media (max-width: 768px) {
    transform: rotate(90deg);
    width: 100vh;
    height: 100vw;
  }
`;

const CloseButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 24px;
  font-weight: bold;
  color: #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.2s ease;
  z-index: ${Z_INDEX.modalClose};

  &:hover {
    background: #f1f5f9;
    transform: scale(1.1);
  }
`;

const StudentsList = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow-y: auto;

  @media (min-width: 1024px) {
    max-height: 500px;
  }

  /* Mobil və tablet görünüşündə sağdakı şagird siyahısını gizlət */
  @media (max-width: 1023px) {
    display: none;
  }
`;

const StudentsTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 16px 0;
`;

const StudentsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Mobile Students List Drawer Components
const MobileStudentsTrigger = styled.button<{ $count: number }>`
  display: none;
  position: fixed;
  bottom: 80px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
  z-index: ${Z_INDEX.fab};
  transition: all 0.3s ease;

  @media (max-width: 1023px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(59, 130, 246, 0.5);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MobileDrawer = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${Z_INDEX.drawer};
  background: rgba(0, 0, 0, 0.5);
  animation: fadeIn 0.2s ease;

  @media (min-width: 1024px) {
    display: none;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const DrawerContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: 70vh;
  background: white;
  border-radius: 20px 20px 0 0;
  padding: 20px;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  @media (max-width: 480px) {
    padding: 16px;
    max-height: 75vh;
  }
`;

const DrawerHandle = styled.div`
  width: 40px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin: 0 auto 16px;
  cursor: pointer;
`;

// NEW: Mobile sticky footer components
const MobileStickyFooter = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 16px;
  box-shadow: 0 -4px 20px rgba(15, 23, 42, 0.1);
  border-radius: 16px 16px 0 0;
  z-index: ${Z_INDEX.sticky || 100};
  margin: 0 -16px -16px -16px;

  @media (min-width: 769px) {
    display: none;
  }
`;

const MobilePrimaryButton = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  background: ${props => props.$disabled
    ? '#f3f4f6'
    : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'};
  color: ${props => props.$disabled ? '#9ca3af' : 'white'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 56px;

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

const MobileSecondaryButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const MobileNavButton = styled.button<{ $disabled?: boolean }>`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  border: 2px solid ${props => props.$disabled ? '#e2e8f0' : '#3b82f6'};
  background: white;
  color: ${props => props.$disabled ? '#9ca3af' : '#3b82f6'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 48px;

  &:active:not(:disabled) {
    background: #f8fafc;
    transform: scale(0.98);
  }
`;

const ZoomControlsArea = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  align-items: center;
`;

const ZoomButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: white;
    border-color: #3b82f6;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const QuestionNumberLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-right: auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #64748b;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
`;

const GradingHeader = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const GradingQuestionLabel = styled.span`
  color: #3b82f6;
  font-weight: 700;
  background: #dbeafe;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 14px;
`;

const CriteriaSection = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
`;

const CriteriaTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CriteriaMaxScore = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  background: #e2e8f0;
  padding: 2px 8px;
  border-radius: 4px;
`;

const CriteriaOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 8px;
`;

const CriteriaButton = styled.button<{ selected: boolean }>`
  padding: 12px 8px;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e2e8f0'};
  border-radius: 8px;
  background: ${props => props.selected ? '#dbeafe' : 'white'};
  color: ${props => props.selected ? '#1d4ed8' : '#64748b'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    background: #f0f9ff;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 10px 6px;
    font-size: 13px;
  }
`;

const TotalDisplay = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 10px;
  text-align: center;
  color: white;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
`;

const TotalLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  opacity: 0.9;
  margin-bottom: 8px;
`;

const TotalValue = styled.div`
  font-size: 32px;
  font-weight: 700;
`;

const InfoIcon = styled.button`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid #3b82f6;
  background: #dbeafe;
  color: #3b82f6;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: #3b82f6;
    color: white;
    transform: scale(1.1);
  }
`;

// Question Image Modal Styles
const ViewQuestionButton = styled.button`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 10px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 11px;
    gap: 4px;
  }

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;

    @media (max-width: 480px) {
      width: 12px;
      height: 12px;
    }
  }
`;

const QuestionImageModal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: ${Z_INDEX.questionModal};
  justify-content: center;
  align-items: center;
  padding: 20px;
  cursor: pointer;
`;

const QuestionModalContent = styled.div`
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: default;

  @media (max-width: 768px) {
    max-width: 100vw;
    max-height: 100vh;
  }
`;

const QuestionModalImage = styled.img`
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;

  @media (max-width: 768px) {
    max-height: 85vh;
  }
`;

const QuestionModalCloseButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 24px;
  font-weight: bold;
  color: #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.2s ease;
  z-index: ${Z_INDEX.questionModalClose};

  &:hover {
    background: #f1f5f9;
    transform: scale(1.1);
  }
`;

const QuestionModalErrorMessage = styled.div`
  text-align: center;
  color: white;
  padding: 40px;
`;

const QuestionModalErrorIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const QuestionModalErrorText = styled.div`
  font-size: 18px;
  line-height: 1.6;
`;

const CriteriaModal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${Z_INDEX.criticalModal};
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const CriteriaModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const CriteriaModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
`;

const CriteriaModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const CriteriaModalClose = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
  }
`;

const CriteriaModalBody = styled.div`
  color: #475569;
  font-size: 14px;
  line-height: 1.6;
`;

const ScoreRangeSection = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 10px;
  border-left: 4px solid #3b82f6;
`;

const ScoreRangeTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ScoreRangeValue = styled.span`
  background: #3b82f6;
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
`;

const ScoreRangeDescription = styled.ul`
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
`;

const ScoreRangeItem = styled.li`
  margin-bottom: 8px;
  color: #475569;
`;



const BuraxilisYoxlamaPage: React.FC<BuraxilisYoxlamaPageProps> = ({ teacher }) => {
  const navigate = useNavigate();
  const { yoxlamaId, questionNumber: urlQuestionNumber } = useParams<{ yoxlamaId: string; questionNumber: string }>();
  const toast = useToast();

  const [assignment, setAssignment] = useState<YoklaAssignment | null>(null);
  const [students, setStudents] = useState<ScannedData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedScore, setSelectedScore] = useState<string | null>(null);
  const [note, setNote] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [gradedStudents, setGradedStudents] = useState<Set<string>>(new Set());
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isStudentLoading, setIsStudentLoading] = useState(false);
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false); // Başlangıçta false - sadece gerçekten gerekli olduğunda true yapılacak
  const [openCriteriaModal, setOpenCriteriaModal] = useState<string | null>(null); // 'content' | 'sequence' | 'grammar' | 'vocabulary' | 'penalty' | null
  const [enterKeyEnabled, setEnterKeyEnabled] = useState(true);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);

  // Mobile students list drawer state
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // NEW: Filter state - GLOBAL (for other operations, admin/teacher filters)
  const [filterType, setFilterType] = useState<'all' | 'ungraded' | 'completed'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // LIST FILTER state - ONLY for sidebar/drawer list UI, separate from global
  const [listFilterType, setListFilterType] = useState<'all' | 'ungraded' | 'completed'>('all');
  const [listSearchQuery, setListSearchQuery] = useState('');

  // Question Image Modal state
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [questionImageUrl, setQuestionImageUrl] = useState<string | null>(null);
  const [questionImageLoading, setQuestionImageLoading] = useState(false);
  const [questionImageError, setQuestionImageError] = useState(false);

  // Q26 üçün özel state-lər
  const [contentScore, setContentScore] = useState<number | null>(null); // 0, 1, 2
  const [sequenceScore, setSequenceScore] = useState<number | null>(null); // 0, 0.5, 1
  const [grammarScore, setGrammarScore] = useState<number | null>(null); // 0, 0.5, 1
  const [vocabularyScore, setVocabularyScore] = useState<number | null>(null); // 0, 0.5, 1
  const [penaltyScore, setPenaltyScore] = useState<number | null>(null); // 0, 0.5, 1

  // Apellyasiya modu state-ləri
  const [isApellyasiyaMode, setIsApellyasiyaMode] = useState(false);
  const [apellyasiyaData, setApellyasiyaData] = useState<any>(null);
  const [apellyasiyaNote, setApellyasiyaNote] = useState<string>('');
  const [previousScore, setPreviousScore] = useState<string>('');

  // Pool assignment lock state
  const [currentLock, setCurrentLock] = useState<QuestionLock | null>(null);
  const [lockWarning, setLockWarning] = useState<string | null>(null);
  const lockExtensionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentLockRef = useRef<{ studentDocId: string; questionNumber: string } | null>(null);

  // Q26 meyar açıqlamaları (JSON-dan)
  const q26CriteriaData = {
    content: {
      title: 'Məzmun/Tapşırığın Yerinə Yetirilməsi',
      maxScore: 2,
      ranges: [
        {
          score: 2,
          description: [
            'Tapşırıq ardıcıl verilmiş 3 şəklə əsaslanan hekayə formasındadır (bəzən hekayə və ya hekayə daxilində dialoqdan ibarətdir).',
            'Namizəd yazı işinə yaradıcı yanaşır (qəhrəmanlara ad verir, təxəyyülə əsaslanır).',
            'Hekayənin məzmunu şəkillərə uyğundur.',
            'Oxucu detallı (təfərrüatlı) məlumat əldə edir.',
            'Yazının həcmi şərtə tamamilə (35 söz və daha çox) uyğundur.'
          ]
        },
        {
          score: 1,
          description: [
            'Tapşırıq əsasən təsvir xarakterlidir (hekayədə yalnız obrazlara adlar verilib).',
            'Məzmun zəif (şəkillərə uyğun olsa da) şəkildə şərh olunub.',
            'Oxucu az məlumat əldə edir.',
            'Nəzərə çarpacaq qədər mövzudan kənaraçıxma və ya ixtisarlar müşahidə olunur.',
            'Yazının həcmi 20-34 sözdür.'
          ]
        },
        {
          score: 0,
          description: [
            'Yazı işi tələb olunan xarici dildə yazılsa da, tamamilə mövzudan kənardır (başqa bir mövzuya uyğun yazılıb).',
            'Şəkillərə (şəkildəki mövzuya) istinad edilməmişdir.',
            'Hekayə anlaşılan deyil (söz yığınından ibarətdir).',
            'Tapşırığın həcmi tələb olunan söz sayından azdır (20 sözdən azdır).',
            'Xətt oxunaqlı deyil.',
            'İşarə qoyulub, ad/soyad yazılıb.'
          ]
        }
      ]
    },
    sequence: {
      title: 'Ardıcıllıq/Məntiqi Rabitə',
      maxScore: 1,
      ranges: [
        {
          score: 1,
          description: [
            'Hekayəni şəklə əsasən başa düşülən səviyyədə yaxşı tərtib edir və aydın şəkildə əlaqələndirə bilir.',
            'Əlavə birləşdirici vasitələrin (birləşdirici zərflər (first, second, unfortunately və s.)) köməyi ilə müstəqil cümlələr, abzaslar arasında əlaqə yaradır.',
            'Fikirlər arasında məntiqi rabitə vardır.'
          ]
        },
        {
          score: 0.5,
          description: [
            'Fikirlər arasında ardıcıllıq var, ancaq əlaqə pozulmuşdur.',
            'Eyni birləşdirici vasitələrdən məhdud sayda istifadə edir.',
            'Fikirlər əsasən durğu işarələrinin (vergül) köməyilə ayrılır və ya birləşir.',
            'Şəklə əsasən ardıcıllıq təmin olunur.'
          ]
        },
        {
          score: 0,
          description: [
            'Yazı işi 0.5 balın tələblərinə cavab vermir.'
          ]
        }
      ]
    },
    grammar: {
      title: 'Qrammatika',
      maxScore: 1,
      ranges: [
        {
          score: 1,
          description: [
            'Yoxlanılan səviyyə üzrə müxtəlif qrammatik forma və sintaktik strukturlardan (sadə və mürəkkəb cümlələr) düzgün və yerində istifadə edir.',
            'Bağlayıcılardan istifadə edərək (so, and, or, but, when, while, since, because və s.) mürəkkəb cümlələr qurur.'
          ]
        },
        {
          score: 0.5,
          description: [
            'Qısa, şablon və ya yarımçıq cümlələr qurur.',
            'Çox sadə qrammatik strukturlardan istifadə edir.'
          ]
        },
        {
          score: 0,
          description: [
            'Yazı işi 0.5 balın tələblərinə cavab vermir.'
          ]
        }
      ]
    },
    vocabulary: {
      title: 'Söz Ehtiyatı',
      maxScore: 1,
      ranges: [
        {
          score: 1,
          description: [
            'Gündəlik ən çox istifadə olunan söz və söz birləşmələrini yerində işlədir.',
            'Leksik təkrarlara yol vermir.',
            'Sinonimlərdən istifadə edir.',
            'Orfoqrafiya qaydalarına əməl edir.'
          ]
        },
        {
          score: 0.5,
          description: [
            'Söz bazası tapşırığı yerinə yetirməyə mane olur.',
            'Təkrarlar çoxdur.'
          ]
        },
        {
          score: 0,
          description: [
            'Yazı işi 0.5 balın tələblərinə cavab vermir.'
          ]
        }
      ]
    },
    penalty: {
      title: 'Çıxılan Bal/Cərimə',
      maxScore: 1,
      ranges: [
        {
          score: 0,
          description: [
            'Çıxılan bal: 0',
            'Leksik, qrammatik, orfoqrafiya üzrə səhvlərin ümumi sayı 0-2-dir.'
          ]
        },
        {
          score: 0.5,
          description: [
            'Çıxılan bal: 0.5',
            'Səhvlərin ümumi sayı 3-5-dir.'
          ]
        },
        {
          score: 1,
          description: [
            'Çıxılan bal: 1',
            'Səhvlərin ümumi sayı 6-dan çoxdur.'
          ]
        }
      ]
    }
  };

  // Dinamik scoreOptions - Q26 üçün genişləndirilmiş seçeneklər
  const scoreOptions = useMemo(() => {
    const baseOptions = [
      { value: '0', label: '0' },
      { value: '1/3', label: '1/3' },
      { value: '1/2', label: '1/2' },
      { value: '2/3', label: '2/3' },
      { value: '1', label: '1' }
    ];

    if (isQ26SpecialGrading(assignment, assignment?.questionNumber)) {
      // Q26 üçün: mevcut puanlar + yeni puanlar
      return [
        ...baseOptions,
        { value: '1.5', label: '1.5' },
        { value: '2', label: '2' },
        { value: '2.5', label: '2.5' },
        { value: '3', label: '3' },
        { value: '3.5', label: '3.5' },
        { value: '4', label: '4' },
        { value: '4.5', label: '4.5' },
        { value: '5', label: '5' }
      ];
    }

    return baseOptions;
  }, [assignment]);

  // Q26 total hesablama
  const calculateQ26Total = (): number => {
    const content = contentScore ?? 0;
    const sequence = sequenceScore ?? 0;
    const grammar = grammarScore ?? 0;
    const vocabulary = vocabularyScore ?? 0;
    const penalty = penaltyScore ?? 0;
    
    const total = content + sequence + grammar + vocabulary - penalty;
    return Math.max(0, Math.min(5, total)); // 0-5 arası məhdudlaşdırma
  };

  // Privacy: Mask student name
  const maskName = (firstName: string, lastName: string) => {
    const maskedFirst = firstName.charAt(0) + '*'.repeat(Math.max(1, firstName.length - 1));
    const maskedLast = '*'.repeat(Math.max(1, lastName.length));
    return `${maskedFirst} ${maskedLast}`;
  };

  // Privacy: Mask student ID
  const maskStudentId = (studentId: string) => {
    if (studentId.length <= 2) return '*'.repeat(studentId.length);
    return studentId.charAt(0) + '*'.repeat(studentId.length - 1);
  };

  // NEW: Helper function to get student score from grading data
  const getStudentScore = (student: ScannedData, questionNumber: string | undefined): number | null => {
    if (!questionNumber) return null;

    const gradingField = `${questionNumber}_c`;
    const gradingData = (student as any)[gradingField];

    if (!gradingData || typeof gradingData !== 'object') return null;

    // Handle Q26 special grading
    if (isQ26SpecialGrading(assignment, questionNumber) && gradingData.criteria) {
      const { content, sequence, grammar, vocabulary, penalty } = gradingData.criteria;
      if (content !== undefined && sequence !== undefined &&
          grammar !== undefined && vocabulary !== undefined) {
        return content + sequence + grammar + vocabulary - (penalty || 0);
      }
      return null;
    }

    // Regular grading
    return gradingData.score ? parseFloat(gradingData.score) : null;
  };

  // Check if this is a pool assignment
  const isPoolAssignment = assignment?.assignmentType === 'pool';

  // Lock helper: acquire lock for a student's question
  const tryAcquireLock = useCallback(async (studentDocId: string, qNum: string): Promise<boolean> => {
    if (!isPoolAssignment || !assignment?.collectionName) return true;

    const result = await acquireLock(
      assignment.collectionName,
      studentDocId,
      qNum,
      teacher.id,
      teacher.name
    );

    if (result.success) {
      setCurrentLock(result.currentLock || null);
      setLockWarning(null);
      currentLockRef.current = { studentDocId, questionNumber: qNum };
      return true;
    } else {
      setLockWarning(result.reason || 'Kilit əldə edilə bilmədi');
      setCurrentLock(result.currentLock || null);
      return false;
    }
  }, [isPoolAssignment, assignment?.collectionName, teacher.id, teacher.name]);

  // Lock helper: release current lock
  const releaseCurrentLock = useCallback(async () => {
    if (!isPoolAssignment || !assignment?.collectionName || !currentLockRef.current) return;

    await releaseLock(
      assignment.collectionName,
      currentLockRef.current.studentDocId,
      currentLockRef.current.questionNumber,
      teacher.id
    );

    currentLockRef.current = null;
    setCurrentLock(null);
    setLockWarning(null);
  }, [isPoolAssignment, assignment?.collectionName, teacher.id]);

  // Lock helper: extend current lock
  const extendCurrentLock = useCallback(async () => {
    if (!isPoolAssignment || !assignment?.collectionName || !currentLockRef.current) return;

    await extendLock(
      assignment.collectionName,
      currentLockRef.current.studentDocId,
      currentLockRef.current.questionNumber,
      teacher.id
    );
  }, [isPoolAssignment, assignment?.collectionName, teacher.id]);

  // Setup lock extension interval on activity
  useEffect(() => {
    if (!isPoolAssignment) return;

    // Clear existing interval
    if (lockExtensionIntervalRef.current) {
      clearInterval(lockExtensionIntervalRef.current);
    }

    // Set up interval to extend lock every 5 minutes while active
    lockExtensionIntervalRef.current = setInterval(() => {
      if (currentLockRef.current) {
        extendCurrentLock();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      if (lockExtensionIntervalRef.current) {
        clearInterval(lockExtensionIntervalRef.current);
      }
    };
  }, [isPoolAssignment, extendCurrentLock]);

  // Release lock on component unmount
  useEffect(() => {
    return () => {
      if (currentLockRef.current && assignment?.collectionName) {
        // Fire and forget - don't await in cleanup
        releaseLock(
          assignment.collectionName,
          currentLockRef.current.studentDocId,
          currentLockRef.current.questionNumber,
          teacher.id
        );
      }
    };
  }, [assignment?.collectionName, teacher.id]);

  const loadCancelledRef = useRef(false);
  // Hər açılışda URL-dən assignment + şagirdləri çək (dashboard state-indən asılı deyil)
  useEffect(() => {
    if (!yoxlamaId || !urlQuestionNumber) {
      setLoading(false);
      return;
    }
    loadCancelledRef.current = false;
    const load = async () => {
      setLoading(true);
      try {
        const assignmentDoc = await getDocFromServer(doc(db, 'yoxla-s', yoxlamaId));
        if (loadCancelledRef.current || !assignmentDoc.exists()) {
          if (!loadCancelledRef.current) setLoading(false);
          return;
        }
        const raw = assignmentDoc.data();
        const isApellyasiya = raw.type === 'apellyasiya';

        if (isApellyasiya) {
          const studentDoc = await getDocFromServer(doc(db, raw.collectionName, raw.studentDocumentId));
          if (loadCancelledRef.current || !studentDoc.exists()) {
            if (!loadCancelledRef.current) setLoading(false);
            return;
          }
          const studentDataObj = studentDoc.data();
          const studentSinif = String(studentDataObj?.grade ?? studentDataObj?.sinif ?? raw.sinif ?? '').trim();
          const assignmentData: YoklaAssignment = {
            id: assignmentDoc.id,
            hocaId: raw.hocaId || '',
            hocaName: raw.hocaName,
            fenn: raw.fenn || '',
            sinif: studentSinif || raw.sinif || '',
            qrup: raw.qrup || raw.variant || studentDataObj?.variant || '',
            variant: raw.variant || studentDataObj?.variant,
            collectionName: raw.collectionName,
            examType: 'buraxilis' as const,
            questionNumber: urlQuestionNumber,
            questionNumbers: [urlQuestionNumber],
            type: 'apellyasiya',
            ...raw
          };
          setAssignment(assignmentData);
          setApellyasiyaData({ id: assignmentDoc.id, ...raw });
          setIsApellyasiyaMode(true);
          const studentData = { id: studentDoc.id, ...studentDataObj, student_id: studentDataObj?.student_id || studentDoc.id } as ScannedData;
          setStudents([studentData]);
          const gradingFieldName = `${urlQuestionNumber}_c`;
          const gradingData: any = studentDataObj[gradingFieldName];
          let prev = '0';
          if (gradingData && typeof gradingData === 'object') {
            if (isQ26SpecialGrading(assignmentData, urlQuestionNumber) && gradingData.criteria) {
              const { content = 0, sequence = 0, grammar = 0, vocabulary = 0, penalty = 0 } = gradingData.criteria;
              prev = (content + sequence + grammar + vocabulary - penalty).toString();
            } else if (gradingData.score) prev = gradingData.score.toString();
          }
          setPreviousScore(prev);
          setGradedStudents(new Set());
          setCurrentIndex(0);
        } else {
          const assignmentData = {
            id: assignmentDoc.id,
            ...raw,
            questionNumber: urlQuestionNumber,
            examType: 'buraxilis' as const
          } as YoklaAssignment;
          const loadedStudents = await getScannedDataByAssignment(assignmentData, teacher.id, { fromServer: true });
          if (loadCancelledRef.current) return;
          const qField = `${urlQuestionNumber}_c`;
          let baseList = loadedStudents.filter((s: ScannedData) => {
            const qValue: any = (s as any)[qField];
            if (!qValue) return true;
            if (typeof qValue === 'object') {
              if (qValue.hocaId === 'admin' && qValue.note?.includes('Boş sual - Admin tərəfindən təsdiqləndi')) return false;
              if (qValue.hocaId && qValue.hocaId !== teacher.id) return false;
            }
            return true;
          });
          const normXdil = (xdil: string | null | undefined) => {
            if (!xdil) return '';
            const n = xdil.trim();
            if (n === 'İngilis' || n === 'ingilis' || n === 'İNGİLİS') return 'İngilis dili';
            if (n === 'Fransız' || n === 'fransız' || n === 'FRANSIZ') return 'Fransız dili';
            if (n === 'Alman' || n === 'alman' || n === 'ALMAN') return 'Alman dili';
            if (n === 'Rus' || n === 'rus' || n === 'RUS') return 'Rus dili';
            return n;
          };
          baseList = [...baseList].sort((a, b) => {
            const xA = normXdil(a.xdil), xB = normXdil(b.xdil);
            if (xA !== xB) return xA.localeCompare(xB);
            return (a.id || a.student_id || '').localeCompare(b.id || b.student_id || '');
          });
          const graded = new Set<string>();
          let lastGradedIndex = -1;
          let firstOldGradingIndex = -1;
          const isQ26 = isQ26SpecialGrading(assignmentData, urlQuestionNumber);
          baseList.forEach((s: ScannedData, i: number) => {
            const g: any = (s as any)[qField];
            if (g && typeof g === 'object' && g.score && g.hocaId === teacher.id) {
              graded.add(s.student_id);
              lastGradedIndex = i;
              if (isQ26 && firstOldGradingIndex === -1 && !g.criteria) firstOldGradingIndex = i;
            }
          });
          const startIndex = isQ26 && firstOldGradingIndex >= 0 ? firstOldGradingIndex : (lastGradedIndex >= 0 && lastGradedIndex < baseList.length - 1 ? lastGradedIndex + 1 : 0);
          setAssignment(assignmentData);
          setStudents(loadedStudents);
          setGradedStudents(graded);
          setCurrentIndex(startIndex);
        }
      } catch (e) {
        console.error('BuraxilisYoxlamaPage load error:', e);
      } finally {
        if (!loadCancelledRef.current) setLoading(false);
      }
    };
    load();
    return () => { loadCancelledRef.current = true; };
  }, [yoxlamaId, urlQuestionNumber, teacher.id, navigate]);

  // Buraxılış üçün: admin "Boş sual" və digər müəllimlərin balladığı şagirdləri lokalda da gizlət
  const filteredStudents = useMemo(() => {
    if (!assignment) return students;

    // Apellyasiya modunda filtreleme yapma, tüm student'ları göster
    if (isApellyasiyaMode) {
      return students;
    }

    const qField = `${assignment.questionNumber}_c`;

    let baseList = students.filter(student => {
      const qValue: any = (student as any)[qField];

      if (!qValue) {
        // Heç kim qiymətləndirməyib – bu müəllim üçün açıqdır
        return true;
      }

      if (qValue && typeof qValue === 'object') {
        const isAdminEmpty =
          qValue.hocaId === 'admin' &&
          qValue.note &&
          qValue.note.includes('Boş sual - Admin tərəfindən təsdiqləndi');

        if (isAdminEmpty) {
          // Admin boş kağıt – bu müəllim heç görməməlidir
          return false;
        }

        // Başqa müəllim ballayıbsa da, bu müəllimin siyahısına düşməməlidir
        if (qValue.hocaId && qValue.hocaId !== teacher.id) {
          return false;
        }
      }

      return true;
    });

    // NEW: Apply filter type (all/ungraded/completed)
    if (filterType === 'ungraded') {
      baseList = baseList.filter(s => !gradedStudents.has(s.student_id));
    } else if (filterType === 'completed') {
      baseList = baseList.filter(s => gradedStudents.has(s.student_id));
    }

    // NEW: Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      baseList = baseList.filter(student => {
        // Get original index for numbering
        const originalIndex = students.indexOf(student);
        const studentNum = (originalIndex + 1).toString();

        // Get name from student - type-safe approach
        let name = '';
        const studentWithFirstName = student as { firstName?: string; lastName?: string; name?: string; surname?: string; student_id: string | number };
        if (studentWithFirstName.firstName && studentWithFirstName.lastName) {
          name = `${studentWithFirstName.firstName} ${studentWithFirstName.lastName}`;
        } else if (studentWithFirstName.name && studentWithFirstName.surname) {
          name = `${studentWithFirstName.name} ${studentWithFirstName.surname}`;
        }

        const nameLower = name.toLowerCase();
        const id = studentWithFirstName.student_id.toString();

        return studentNum.includes(query) || nameLower.includes(query) || id.includes(query);
      });
    }

    // Sıralama: Önce normalize edilmiş xdil'e göre (İngilis dili önce, İngilis sonra), sonra document ID'ye göre
    // normalizeXdil fonksiyonu
    const normalizeXdil = (xdil: string | null | undefined): string => {
      if (!xdil) return '';
      const normalized = xdil.trim();
      if (normalized === 'İngilis' || normalized === 'ingilis' || normalized === 'İNGİLİS') return 'İngilis dili';
      if (normalized === 'Fransız' || normalized === 'fransız' || normalized === 'FRANSIZ') return 'Fransız dili';
      if (normalized === 'Alman' || normalized === 'alman' || normalized === 'ALMAN') return 'Alman dili';
      if (normalized === 'Rus' || normalized === 'rus' || normalized === 'RUS') return 'Rus dili';
      return normalized;
    };

    return baseList.sort((a, b) => {
      // Önce normalize edilmiş xdil'e göre sırala
      const xdilA = normalizeXdil(a.xdil);
      const xdilB = normalizeXdil(b.xdil);

      // Aynı normalize edilmiş xdil ise, orijinal xdil'e göre sırala (İngilis dili önce, İngilis sonra)
      if (xdilA === xdilB) {
        const origXdilA = (a.xdil || '').trim();
        const origXdilB = (b.xdil || '').trim();

        // Eğer biri "İngilis dili" diğeri "İngilis" ise, "İngilis dili" önce gelsin
        if (origXdilA === 'İngilis dili' && origXdilB === 'İngilis') return -1;
        if (origXdilA === 'İngilis' && origXdilB === 'İngilis dili') return 1;
        if (origXdilA === 'Fransız dili' && origXdilB === 'Fransız') return -1;
        if (origXdilA === 'Fransız' && origXdilB === 'Fransız dili') return 1;
        if (origXdilA === 'Alman dili' && origXdilB === 'Alman') return -1;
        if (origXdilA === 'Alman' && origXdilB === 'Alman dili') return 1;
        if (origXdilA === 'Rus dili' && origXdilB === 'Rus') return -1;
        if (origXdilA === 'Rus' && origXdilB === 'Rus dili') return 1;

        // Aynı orijinal xdil ise, document ID'ye göre sırala
        const idA = a.id || a.student_id || '';
        const idB = b.id || b.student_id || '';
        return idA.localeCompare(idB);
      }
      
      // Farklı xdil ise, normalize edilmiş xdil'e göre sırala
      return xdilA.localeCompare(xdilB);
    });
  }, [students, assignment, teacher.id, isApellyasiyaMode, filterType, searchQuery]);

  // LIST FILTERED STUDENTS - ONLY for sidebar/drawer list UI display
  // Uses filteredStudents as BASE (includes global admin/teacher filters)
  // Then applies additional list-specific filters (all/ungraded/completed + search)
  const listFilteredStudents = useMemo(() => {
    if (!filteredStudents || filteredStudents.length === 0) return [];

    let baseList = [...filteredStudents];  // Use filteredStudents as BASE!

    // Apply list filter type (all/ungraded/completed) - ADDITIONAL to global filters
    if (listFilterType === 'ungraded') {
      baseList = baseList.filter(s => !gradedStudents.has(s.student_id));
    } else if (listFilterType === 'completed') {
      baseList = baseList.filter(s => gradedStudents.has(s.student_id));
    }

    // Apply list search query
    if (listSearchQuery.trim()) {
      const query = listSearchQuery.toLowerCase();
      baseList = baseList.filter(student => {
        // Get original index for numbering in filteredStudents (global data)
        const originalIndex = filteredStudents.findIndex(s => s.student_id === student.student_id);
        const studentNum = (originalIndex + 1).toString();

        // Get name from student - type-safe approach
        let name = '';
        const studentWithFirstName = student as { firstName?: string; lastName?: string; name?: string; surname?: string; student_id: string | number };
        if (studentWithFirstName.firstName && studentWithFirstName.lastName) {
          name = `${studentWithFirstName.firstName} ${studentWithFirstName.lastName}`;
        } else if (studentWithFirstName.name && studentWithFirstName.surname) {
          name = `${studentWithFirstName.name} ${studentWithFirstName.surname}`;
        }

        const nameLower = name.toLowerCase();
        const id = studentWithFirstName.student_id.toString();

        return studentNum.includes(query) || nameLower.includes(query) || id.includes(query);
      });
    }

    return baseList;  // Keep filteredStudents sorting
  }, [filteredStudents, listFilterType, listSearchQuery, gradedStudents, students]);

  // Apellyasiya modunu yüklə (yalnız state-dən gələn assignment'lar üçün)
  useEffect(() => {
    const loadApellyasiyaData = async () => {
      // URL'den yüklenen apellyasiya assignment'ları için bu useEffect çalışmamalı
      // çünkü zaten loadAssignmentData içinde set edildi
      if (!assignment || (assignment as any).type !== 'apellyasiya') {
        // Eğer assignment apellyasiya değilse ve isApellyasiyaMode true ise, false yap
        if (isApellyasiyaMode) {
          setIsApellyasiyaMode(false);
          setApellyasiyaData(null);
        }
        return;
      }

      // Eğer apellyasiyaData zaten set edilmişse (URL'den yüklendi), tekrar query yapma
      if (apellyasiyaData && apellyasiyaData.id === assignment.id) {
        console.log('📝 Apellyasiya data zaten yüklü, query atlanıyor');
        return;
      }

      try {
        // State'den gelen apellyasiya assignment'ları için query yap
        const q = query(
          collection(db, 'yoxla-s'),
          where('type', '==', 'apellyasiya'),
          where('hocaId', '==', teacher.id),
          where('questionNumber', '==', assignment.questionNumber),
          where('apellyasiya_status', '==', 'pending')
        );
        
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const apData = querySnapshot.docs[0].data();
          setApellyasiyaData({ id: querySnapshot.docs[0].id, ...apData });
          setIsApellyasiyaMode(true);
          
          // Gerçek əvvəlki balı öğrenci dokümanından al
          try {
            if (apData.studentDocumentId && apData.collectionName && assignment.questionNumber) {
              const studentDoc = await getDoc(doc(db, apData.collectionName, apData.studentDocumentId));
              if (studentDoc.exists()) {
                const gradingFieldName = `${assignment.questionNumber}_c`;
                const gradingData: any = studentDoc.data()[gradingFieldName];
                
                let actualPreviousScore = '0';
                if (gradingData && typeof gradingData === 'object') {
                  // Q26 üçün: criteria varsa toplam puanı hesabla
                  if (isQ26SpecialGrading(assignment, assignment.questionNumber) && gradingData.criteria) {
                    const { content = 0, sequence = 0, grammar = 0, vocabulary = 0, penalty = 0 } = gradingData.criteria;
                    const total = content + sequence + grammar + vocabulary - penalty;
                    actualPreviousScore = total.toString();
                  } else if (gradingData.score) {
                    // Normal format: score-u istifadə et
                    actualPreviousScore = gradingData.score.toString();
                  }
                }
                
                setPreviousScore(actualPreviousScore);
                console.log('📊 Gerçek əvvəlki bal (useEffect):', actualPreviousScore);
              } else {
                // Fallback: apellyasiya dokümanındakı dəyəri istifadə et
                if (apData.əvvəlki_yoxlama) {
                  setPreviousScore(apData.əvvəlki_yoxlama.score || '0');
                }
              }
            } else {
              // Fallback: apellyasiya dokümanındakı dəyəri istifadə et
              if (apData.əvvəlki_yoxlama) {
                setPreviousScore(apData.əvvəlki_yoxlama.score || '0');
              }
            }
          } catch (error) {
            console.error('❌ Əvvəlki bal yüklənərkən xəta:', error);
            // Fallback: apellyasiya dokümanındakı dəyəri istifadə et
            if (apData.əvvəlki_yoxlama) {
              setPreviousScore(apData.əvvəlki_yoxlama.score || '0');
            }
          }
        } else {
          setIsApellyasiyaMode(false);
          setApellyasiyaData(null);
        }
      } catch (error) {
        console.error('Apellyasiya məlumatı yüklənərkən xəta:', error);
        setIsApellyasiyaMode(false);
        setApellyasiyaData(null);
      }
    };

    loadApellyasiyaData();
  }, [assignment, teacher.id, apellyasiyaData, isApellyasiyaMode]);

  useEffect(() => {
    if (!assignment) {
      return;
    }
    
    // Apellyasiya modunda direkt ilk student'a git (filteredStudents boş olsa bile)
    if (isApellyasiyaMode) {
      if (filteredStudents && filteredStudents.length > 0) {
        setCurrentIndex(0);
      }
      return;
    }

    if (!filteredStudents || filteredStudents.length === 0) {
      return;
    }

    // Check which students are already graded by THIS teacher and resume from last one
    const graded = new Set<string>();
    let lastGradedIndex = -1;
    const isQ26 = isQ26SpecialGrading(assignment, assignment.questionNumber);
    let firstOldGradingIndex = -1; // Q26 için: eski normal modda yoklanmış ilk öğrenci
    
    filteredStudents.forEach((student, index) => {
      const gradingData = (student as any)[`${assignment.questionNumber}_c`];
      if (
        gradingData &&
        typeof gradingData === 'object' &&
        gradingData.score &&
        gradingData.hocaId === teacher.id
      ) {
        graded.add(student.student_id);
        lastGradedIndex = index;
        
        // Q26 için: Eski normal modda yoklanmış öğrenciyi bul (score var ama criteria yok)
        if (isQ26 && firstOldGradingIndex === -1 && !gradingData.criteria) {
          firstOldGradingIndex = index;
        }
      }
    });
    setGradedStudents(graded);

    // Q26 için özel mantık: Eğer eski normal modda yoklanmış öğrenci varsa, ona git
    if (isQ26 && firstOldGradingIndex >= 0) {
      setCurrentIndex(firstOldGradingIndex);
    } else if (lastGradedIndex >= 0 && lastGradedIndex < filteredStudents.length - 1) {
      // Normal mantık: Son yoklanmış öğrenciden sonraki öğrenciye git
      setCurrentIndex(lastGradedIndex + 1);
    } else {
      // Hiç yoklama yoksa ilk öğrenciye git
      setCurrentIndex(0);
    }
  }, [assignment, filteredStudents, teacher.id, isApellyasiyaMode]);

  const currentStudent = assignment ? filteredStudents[currentIndex] : undefined;
  const questionNumber = assignment?.questionNumber!;
  const questionNumberDisplay = questionNumber ? questionNumber.replace(/^Q/i, '') : '';

  // Determine grading mode (for shortcuts modal)
  const gradingMode = assignment && questionNumber && isQ26SpecialGrading(assignment, questionNumber) ? 'q26' : 'normal';

  // For buraxılış, question images are stored directly in question field (e.g., Q78, Q79)
  const questionData = currentStudent?.[questionNumber] as QuestionData;
  const imageUrl = typeof questionData === 'string' ? questionData : questionData?.path;

  // Əgər bu sual üçün şəkil yoxdursa, şagird yükləmə statusunu sıfırla
  useEffect(() => {
    if (!imageUrl) {
      setIsStudentLoading(false);
    }
  }, [imageUrl]);

  // Acquire lock when currentStudent changes (for pool assignments)
  useEffect(() => {
    if (!isPoolAssignment || !assignment?.collectionName || !currentStudent?.id) return;

    const acquireLockForStudent = async () => {
      // Release previous lock if any
      if (currentLockRef.current && currentLockRef.current.studentDocId !== currentStudent.id) {
        await releaseCurrentLock();
      }

      // Try to acquire lock for new student
      const studentDocId = currentStudent.id!;
      const qNum = assignment.questionNumber!;
      const success = await tryAcquireLock(studentDocId, qNum);
      if (!success) {
        // Show warning but don't block - teacher can still view
        toast.warning('Kilit xəbərdarlığı', lockWarning || 'Bu sual hazırda başqa müəllim tərəfindən yoxlanılır');
      }
    };

    acquireLockForStudent();
  }, [currentStudent?.id, assignment?.questionNumber, isPoolAssignment, assignment?.collectionName]);

  // Hər dəfə currentStudent dəyişəndə, əvvəlki balı və qeydi form-a yüklə
  useEffect(() => {
    if (!assignment || !currentStudent) return;

    const gradingFieldName = `${assignment.questionNumber}_c`;
    const gradingData: any = (currentStudent as any)[gradingFieldName];

    const isQ26 = isQ26SpecialGrading(assignment, assignment.questionNumber);

    if (isQ26) {
      // Q26 üçün: criteria obyektindən meyarları yüklə
      if (gradingData && typeof gradingData === 'object') {
        if (gradingData.criteria) {
          // Yeni Q26 formatı (criteria ile)
          setContentScore(gradingData.criteria.content ?? null);
          setSequenceScore(gradingData.criteria.sequence ?? null);
          setGrammarScore(gradingData.criteria.grammar ?? null);
          setVocabularyScore(gradingData.criteria.vocabulary ?? null);
          setPenaltyScore(gradingData.criteria.penalty ?? null);
          setNote(gradingData.note || '');
        } else if (gradingData.score) {
          // Eski normal modda verilmiş yoklama - preview olarak göster
          // Criteria'ları null bırak ama score'u selectedScore'a yükle
          setContentScore(null);
          setSequenceScore(null);
          setGrammarScore(null);
          setVocabularyScore(null);
          setPenaltyScore(null);
          setSelectedScore(gradingData.score);
          setNote(gradingData.note || '');
        } else {
          // Hiç yoklama yok
          setContentScore(null);
          setSequenceScore(null);
          setGrammarScore(null);
          setVocabularyScore(null);
          setPenaltyScore(null);
          setSelectedScore(null);
          setNote('');
        }
      } else {
        setContentScore(null);
        setSequenceScore(null);
        setGrammarScore(null);
        setVocabularyScore(null);
        setPenaltyScore(null);
        setSelectedScore(null);
        setNote('');
      }
    } else {
      // Normal sorular üçün: mevcut məntiq
      if (gradingData && typeof gradingData === 'object' && gradingData.score) {
        setSelectedScore(gradingData.score);
        setNote(gradingData.note || '');
      } else {
        setSelectedScore(null);
        setNote('');
      }
      // Q26 state-lərini təmizlə
      setContentScore(null);
      setSequenceScore(null);
      setGrammarScore(null);
      setVocabularyScore(null);
      setPenaltyScore(null);
    }
  }, [assignment, currentStudent]);

  // Q26 üçün: criteria state-ləri dəyişəndə total-i yenilə
  useEffect(() => {
    if (isQ26SpecialGrading(assignment, assignment?.questionNumber)) {
      const total = calculateQ26Total();
      setSelectedScore(total > 0 ? total.toString() : null);
    }
  }, [contentScore, sequenceScore, grammarScore, vocabularyScore, penaltyScore, assignment]);

  // Enter key ayarını yükle - mount edildiğinde
  useEffect(() => {
    setEnterKeyEnabled(getEnterKeyShortcutPreference());
  }, []);

  // Enter tuşu ile təsdiqləmə keyboard event listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Sadece Enter tuşu ve aktif modda çalışır
      if (e.key !== 'Enter' || !enterKeyEnabled) {
        return;
      }

      // Textarea içindeysek Enter'a izin ver (yeni satır)
      const target = e.target as HTMLElement;
      if (target.tagName === 'TEXTAREA') {
        return;
      }

      // Confirm button'ı bul
      const confirmButton = document.querySelector('[data-confirm-button]') as HTMLButtonElement;
      if (confirmButton && !confirmButton.disabled) {
        e.preventDefault();
        confirmButton.click();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enterKeyEnabled]);

  const handleSaveAndNext = async () => {
    if (!assignment || !currentStudent) {
      return;
    }

    // Apellyasiya modu üçün xüsusi validasiya
    if (isApellyasiyaMode) {
      if (!apellyasiyaNote.trim()) {
        toast.warning('Qeyd lazımdır', 'Zəhmət olmasa apellyasiya qeydini yazın (ZORUNLU)');
        return;
      }
      if (!selectedScore) {
        toast.warning('Bal seçin', 'Zəhmət olmasa bal seçin');
        return;
      }
    }

    const isQ26 = isQ26SpecialGrading(assignment, questionNumber);

    // Validasiya
    if (isQ26) {
      // Q26 üçün: bütün 4 meyar seçilməlidir (cərimə ixtiyari, default 0)
      if (contentScore === null || sequenceScore === null || grammarScore === null || vocabularyScore === null) {
        toast.warning('Meyarlar seçilməyib', 'Zəhmət olmasa bütün meyarları seçin (Məzmun, Ardıcıllıq, Qrammatika, Söz Ehtiyatı)');
        return;
      }
    } else {
      // Normal sorular üçün: mevcut validasiya
      if (!selectedScore) {
        toast.warning('Bal seçin', 'Zəhmət olmasa bal seçin');
        return;
      }
    }

    try {
      setSaving(true);

      // Save grading data to Firebase
      const gradingFieldName = `${questionNumber}_c`;
      const studentDocRef = doc(db, assignment.collectionName!, currentStudent.id!);
      
      let gradingData: any;

      if (isQ26) {
        // Q26 üçün özel format: criteria obyekti ilə
        const calculatedTotal = calculateQ26Total();
        gradingData = {
          score: calculatedTotal.toString(), // Otomatik hesablanmış total
          hocaId: teacher.id,
          hoca: teacher.name,
          timestamp: Timestamp.now(),
          criteria: {
            content: contentScore,
            sequence: sequenceScore,
            grammar: grammarScore,
            vocabulary: vocabularyScore,
            penalty: penaltyScore ?? 0
          },
          ...(note.trim() && { note: note.trim() })
        };
      } else {
        // Normal sorular üçün: mevcut format
        gradingData = {
          score: selectedScore,
          hocaId: teacher.id,
          hoca: teacher.name,
          timestamp: Timestamp.now(),
          ...(note.trim() && { note: note.trim() })
        };
      }
      
      // Apellyasiya modu üçün xüsusi işləmə
      if (isApellyasiyaMode && apellyasiyaData) {
        const newScore = isQ26 ? calculateQ26Total().toString() : (selectedScore || '0');
        if (!newScore) {
          toast.warning('Bal seçin', 'Zəhmət olmasa bal seçin');
          return;
        }
        await confirmApellyasiya(
          apellyasiyaData.id,
          currentStudent.id!,
          questionNumber,
          assignment.collectionName!,
          apellyasiyaNote.trim(),
          newScore,
          previousScore,
          teacher.id,
          teacher.name
        );
        
        // Local state-ə də yaz
        const updatedGradingData = {
          ...gradingData,
          apellyasiya_note: apellyasiyaNote.trim(),
          apellyasiya_result: true,
          previous_score: previousScore,
          new_score: newScore,
          apellyasiya_status: true,
          apellyasiya_onaylanma_tarihi: Timestamp.now(),
          apellyasiya_timestamp: Timestamp.now()
        };
        
        setStudents(prev =>
          prev.map(s =>
            s.id === currentStudent.id
              ? ({ ...s, [gradingFieldName]: updatedGradingData } as ScannedData)
              : s
          )
        );
        
        // Apellyasiya data state'ini güncelle (apellyasiya_result = true)
        setApellyasiyaData((prev: any) => ({
          ...prev,
          apellyasiya_result: true,
          apellyasiya_status: 'completed',
          apellyasiya_nəticəsi: {
            apellyasiya_note: apellyasiyaNote.trim(),
            apellyasiya_result: true,
            previous_score: previousScore,
            new_score: newScore,
            apellyasiya_status: true,
            apellyasiya_onaylanma_tarihi: Timestamp.now()
          }
        }));
        
        // Apellyasiya qeydini temizle
        setApellyasiyaNote('');

        // Növbəti apellyasiyanı yoxla
        try {
          const nextApellyasiya = await getNextPendingApellyasiya(
            assignment.collectionName!,
            teacher.id,
            apellyasiyaData.id
          );

          if (nextApellyasiya) {
            // Növbəti apellyasiya tapıldı - ona keç
            toast.success('Apellyasiya təsdiqləndi!', 'Növbəti apellyasiyaya keçilir...', 2000);

            // Student document-ini yüklə
            const studentDoc = await getDoc(doc(db, nextApellyasiya.collectionName, nextApellyasiya.studentDocumentId));
            
            if (!studentDoc.exists()) {
              console.error('❌ Şagird sənədi tapılmadı:', nextApellyasiya.studentDocumentId);
              toast.error('Xəta', 'Şagird sənədi tapılmadı. Dashboard-a yönləndirilir...');
              setTimeout(() => {
                navigate('/teacher/dashboard');
              }, 1500);
              return;
            }

            const studentData = {
              id: studentDoc.id,
              ...studentDoc.data(),
              student_id: studentDoc.data().student_id || studentDoc.id
            } as ScannedData;

            // Yeni assignment yarat
            const nextAssignment: YoklaAssignment = {
              id: nextApellyasiya.id,
              questionNumber: nextApellyasiya.questionNumber,
              questionNumbers: [nextApellyasiya.questionNumber],
              collectionName: nextApellyasiya.collectionName,
              sinif: String(studentData.grade || ''),
              variant: studentData.variant || '',
              qrup: studentData.variant || '',
              fenn: nextApellyasiya.fenn,
              examType: 'buraxilis' as const,
              hocaId: nextApellyasiya.hocaId,
              hocaName: nextApellyasiya.hocaName
            };

            // Növbəti apellyasiyaya navigate et
            setTimeout(() => {
              navigate(`/dashboard/buraxilis-yoxlama/${nextApellyasiya.id}/${nextApellyasiya.questionNumber}`, {
                state: { assignment: nextAssignment, students: [studentData] }
              });
            }, 1500);
          } else {
            // Növbəti apellyasiya yoxdur - dashboard-a qayıt
            toast.success('Apellyasiya təsdiqləndi!', 'Bütün apellyasiyalar tamamlandı. Dashboard-a yönləndirilir...', 2000);
            setTimeout(() => {
              navigate('/teacher/dashboard');
            }, 1500);
          }
        } catch (error) {
          console.error('❌ Növbəti apellyasiya yüklənərkən xəta:', error);
          toast.error('Xəta', 'Növbəti apellyasiya yüklənərkən xəta baş verdi. Dashboard-a yönləndirilir...');
          setTimeout(() => {
            navigate('/teacher/dashboard');
          }, 1500);
        }
        
        // Apellyasiya modunda next student'a geçme, direkt return
        // setSaving(false) finally blokunda çağırılacaq
        return;
      } else {
      await updateDoc(studentDocRef, {
        [gradingFieldName]: gradingData
      });

      // Local state-ə də yaz ki, yenidən açanda Q##_c dəyəri olsun
      setStudents(prev =>
        prev.map(s =>
          s.id === currentStudent.id
            ? ({ ...s, [gradingFieldName]: gradingData } as ScannedData)
            : s
        )
      );

      // Show success notification - YoxlamaPage ilə eyni mesaj
      toast.success('Dəyərləndirmə təsdiqləndi!', `${currentIndex + 2 <= filteredStudents.length ? `${filteredStudents.length - currentIndex - 1} şagird qaldı` : 'Bütün şagirdlər bitdi'}`, 2000);

      // Release lock after successful save (for pool assignments)
      if (isPoolAssignment) {
        await releaseCurrentLock();
      }
      }

      // Mark as graded
      setGradedStudents(prev => new Set([...prev, currentStudent.student_id]));

      // Move to next student
      if (currentIndex < filteredStudents.length - 1) {
        setCurrentIndex(currentIndex + 1);
        // Clear inputs and reset states
        if (isQ26) {
          setContentScore(null);
          setSequenceScore(null);
          setGrammarScore(null);
          setVocabularyScore(null);
          setPenaltyScore(null);
        }
        setSelectedScore(null);
        setNote('');
        setImageLoading(true);
        handleResetZoom();
      } else {
        // Last student - clear inputs
        if (isQ26) {
          setContentScore(null);
          setSequenceScore(null);
          setGrammarScore(null);
          setVocabularyScore(null);
          setPenaltyScore(null);
        }
        setSelectedScore(null);
        setNote('');
      }
    } catch (error) {
      console.error('❌ Error saving grade:', error);
      toast.error('Xəta baş verdi', 'Qiymət yadda saxlanılarkən xəta baş verdi. İnternet bağlantısını yoxlayın.');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (currentIndex < filteredStudents.length - 1) {
      setIsStudentLoading(true);
      setCurrentIndex(currentIndex + 1);
      // Clear inputs and reset states
      const isQ26 = isQ26SpecialGrading(assignment, questionNumber);
      if (isQ26) {
        setContentScore(null);
        setSequenceScore(null);
        setGrammarScore(null);
        setVocabularyScore(null);
        setPenaltyScore(null);
      }
      setSelectedScore(null);
      setNote('');
      setImageLoading(true);
      handleResetZoom();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setIsStudentLoading(true);
      setCurrentIndex(currentIndex - 1);
      // Clear inputs and reset states
      const isQ26 = isQ26SpecialGrading(assignment, questionNumber);
      if (isQ26) {
        setContentScore(null);
        setSequenceScore(null);
        setGrammarScore(null);
        setVocabularyScore(null);
        setPenaltyScore(null);
      }
      setSelectedScore(null);
      setNote('');
      setImageLoading(true);
      handleResetZoom();
    }
  };


  const handleBack = () => {
    navigate('/teacher/dashboard');
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 1, 5));
    setIsZoomed(true);
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => {
      const newLevel = Math.max(prev - 1, 1);
      if (newLevel <= 1) {
        setIsZoomed(false);
        return 1;
      }
      return newLevel;
    });
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setIsZoomed(false);
  };

  const handleOpenQuestionModal = async () => {
    if (!assignment || !currentStudent) return;

    try {
      setIsQuestionModalOpen(true);
      setQuestionImageLoading(true);
      setQuestionImageError(false);

      // Get the exam ID from the assignment's collectionName
      // We need to find the exam document that matches this collection
      const examsQuery = query(
        collection(db, 'exams'),
        where('collection', '==', assignment.collectionName)
      );
      const examsSnapshot = await getDocs(examsQuery);

      if (examsSnapshot.empty) {
        setQuestionImageError(true);
        setQuestionImageLoading(false);
        return;
      }

      const examDoc = examsSnapshot.docs[0];
      const examId = examDoc.id;

      // Get current student's variant (default to 'A' if not specified)
      const variant = currentStudent.variant || 'A';

      // Get current student's grade (parse from string or number)
      const studentGrade = typeof currentStudent.grade === 'number'
        ? currentStudent.grade
        : parseInt(currentStudent.grade as string);

      // Get question image URL with variant and grade (grade-specific storage)
      const metadata = await getQuestionImageUrl(examId, questionNumber, variant, studentGrade);

      if (metadata && metadata.url) {
        setQuestionImageUrl(metadata.url);
      } else {
        setQuestionImageError(true);
      }
    } catch (error) {
      console.error('Error loading question image:', error);
      setQuestionImageError(true);
    } finally {
      setQuestionImageLoading(false);
    }
  };

  const handleCloseQuestionModal = () => {
    setIsQuestionModalOpen(false);
    setQuestionImageUrl(null);
    setQuestionImageError(false);
  };

  // Effektiv toplam (admin və başqa hocalar tərəfindən qiymətləndirilənlər çıxılaraq)
  const calculateEffectiveTotal = () => {
    if (!assignment) return filteredStudents.length;
    const qField = `${assignment.questionNumber}_c`;
    let effectiveTotal = 0;

    filteredStudents.forEach(student => {
      const qValue: any = (student as any)[qField];
      if (!qValue) {
        // Heç kim qiymətləndirməyib – bu müəllim üçün açıq
        effectiveTotal++;
      } else if (qValue && typeof qValue === 'object' && qValue.hocaId === teacher.id) {
        // Bu müəllim qiymətləndirib – onun progressinə daxildir
        effectiveTotal++;
      } else {
        // Admin və ya başqa hoca – bu müəllimin progressindən çıx
      }
    });

    return effectiveTotal;
  };

  const effectiveTotal = calculateEffectiveTotal();

  // Faktiki qiymətləndirilmiş şagird sayı (həmişə ən son Q##_c məlumatına görə)
  const gradedCount = useMemo(() => {
    if (!assignment) return 0;
    const qField = `${assignment.questionNumber}_c`;
    return filteredStudents.filter(student => {
      const qValue: any = (student as any)[qField];
      return (
        qValue &&
        typeof qValue === 'object' &&
        qValue.score &&
        qValue.hocaId === teacher.id
      );
    }).length;
  }, [filteredStudents, assignment, teacher.id]);

  const isCurrentStudentGraded =
    !!assignment &&
    !!currentStudent &&
    (() => {
      const qField = `${assignment.questionNumber}_c`;
      const qValue: any = (currentStudent as any)[qField];
      return (
        qValue &&
        typeof qValue === 'object' &&
        qValue.score &&
        qValue.hocaId === teacher.id
      );
    })();

  const progress = effectiveTotal > 0 ? (gradedCount / effectiveTotal) * 100 : 0;
  // Tamamlanmış sayılması üçün: 
  // 1. Loading tamamlanmış olmalı
  // 2. Atama yüklənmiş olmalı
  // 3. Real şagird siyahısı boş olmamalıdır
  // 4. Effective total > 0 olmalı (gerçekten öğrenci var)
  // 5. Apellyasiya modunda: apellyasiya_result === true olmalı (apellyasiya_nəticəsi içinde veya direkt)
  // 6. Normal modda: gradedCount === effectiveTotal olmalı
  const apellyasiyaResult = apellyasiyaData?.apellyasiya_result === true || 
                             apellyasiyaData?.apellyasiya_nəticəsi?.apellyasiya_result === true ||
                             apellyasiyaData?.apellyasiya_status === 'completed';
  const isCompleted = !loading && 
                      !!assignment && 
                      filteredStudents.length > 0 && 
                      effectiveTotal > 0 &&
                      (
                        (isApellyasiyaMode && apellyasiyaResult) ||
                        (!isApellyasiyaMode && gradedCount === effectiveTotal)
                      );

  // Completion mesajını kontrol et - sadece gerçekten tamamlandığında göster
  // Bu useEffect effectiveTotal ve gradedCount'tan sonra çalışmalı
  useEffect(() => {
    if (!assignment || loading) {
      setShowCompletionMessage(false);
      return;
    }

    const isApellyasiyaAssignment = isApellyasiyaMode || (assignment as any)?.type === 'apellyasiya';
    
    if (isApellyasiyaAssignment) {
      // Apellyasiya modunda sadece apellyasiya_result === true ise completion mesajını göster
      const apellyasiyaResultCheck = apellyasiyaData?.apellyasiya_result === true || 
                                     apellyasiyaData?.apellyasiya_nəticəsi?.apellyasiya_result === true ||
                                     apellyasiyaData?.apellyasiya_status === 'completed';
      setShowCompletionMessage(apellyasiyaResultCheck === true);
    } else {
      // Normal modda: sadece gerçekten tamamlandığında göster
      const reallyCompleted = !loading && 
                              filteredStudents.length > 0 && 
                              effectiveTotal > 0 &&
                              gradedCount === effectiveTotal;
      setShowCompletionMessage(reallyCompleted);
    }
  }, [isApellyasiyaMode, assignment, apellyasiyaData, loading, filteredStudents.length, effectiveTotal, gradedCount]);

  const handleReturnToReviews = () => {
    setShowCompletionMessage(false);
    // İlk şagirdə qayıt
    setCurrentIndex(0);
  };

  // YoxlamaPage-dəki kimi: ümumi yüklənmə spinneri
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
          padding: '40px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            border: '6px solid #e0e7ff',
            borderTop: '6px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{ 
            fontSize: '20px', 
            color: '#3b82f6', 
            fontWeight: '600',
            textAlign: 'center'
          }}>
            Şagird məlumatları yüklənir...
          </div>
          <div style={{
            fontSize: '14px',
            color: '#60a5fa',
            fontWeight: '500'
          }}>
            Lütfən gözləyin
          </div>
        </div>
      </div>
    );
  }

  // Completion mesajı: Sadece showCompletionMessage true ise göster (bir anlık görünmeyi engellemek için)
  // showCompletionMessage useEffect içinde sıkı kontrollerle set ediliyor
  if (showCompletionMessage && isCompleted) {
    return (
      <Container>
        <Content>
          <CompletionMessage>
            <h2>✅ Təbriklər!</h2>
            <p>
              {isApellyasiyaMode ? (
                <>
                  Apellyasiya təsdiqləndi və{' '}
                  {questionNumberDisplay ? `Sual ${questionNumberDisplay}` : questionNumber}{' '}
                  sualı üçün qiymətləndirmə tamamlandı
                </>
              ) : (
                <>
              Bütün şagirdlərin{' '}
              {questionNumberDisplay ? `Sual ${questionNumberDisplay}` : questionNumber}{' '}
              sualını qiymətləndirdiniz
                </>
              )}
            </p>
            {/* Apellyasiya nəticəsi detayları */}
            {isApellyasiyaMode && currentStudent && assignment && (() => {
              const gradingFieldName = `${assignment.questionNumber}_c`;
              const gradingData: any = (currentStudent as any)[gradingFieldName];
              if (gradingData && gradingData.apellyasiya_status === true) {
                return (
                  <div style={{
                    marginTop: '20px',
                    padding: '16px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    border: '2px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '12px', color: 'white' }}>
                      📝 Apellyasiya Nəticəsi:
                    </div>
                    {gradingData.previous_score && gradingData.new_score && (
                      <div style={{ fontSize: '14px', marginBottom: '8px', color: 'white' }}>
                        <strong>Əvvəlki bal:</strong> {gradingData.previous_score} → 
                        <strong style={{ 
                          color: gradingData.previous_score !== gradingData.new_score ? '#fef3c7' : 'white',
                          marginLeft: '4px'
                        }}>
                          Yeni bal: {gradingData.new_score}
                        </strong>
                        {gradingData.previous_score !== gradingData.new_score && (
                          <span style={{ marginLeft: '8px', color: '#fef3c7', fontWeight: '600' }}>
                            (Dəyişdi)
                          </span>
                        )}
                      </div>
                    )}
                    {gradingData.apellyasiya_note && (
                      <div style={{ fontSize: '14px', marginBottom: '8px', color: 'white' }}>
                        <strong>Müəllim qeydi:</strong> {gradingData.apellyasiya_note}
                      </div>
                    )}
                    {gradingData.apellyasiya_hoca && gradingData.apellyasiya_hocaId !== gradingData.hocaId && (
                      <div style={{ fontSize: '14px', marginBottom: '8px', color: 'white' }}>
                        <strong>Apellyasiyanı yoxlayan:</strong> {gradingData.apellyasiya_hoca}
                      </div>
                    )}
                    {gradingData.apellyasiya_onaylanma_tarihi && (
                      <div style={{ fontSize: '13px', marginTop: '8px', color: 'rgba(255, 255, 255, 0.9)' }}>
                        Təsdiqlənmə tarixi: {new Date(
                          gradingData.apellyasiya_onaylanma_tarihi.toDate ? 
                          gradingData.apellyasiya_onaylanma_tarihi.toDate() : 
                          gradingData.apellyasiya_onaylanma_tarihi
                        ).toLocaleString('az-AZ')}
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })()}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {!isApellyasiyaMode && (
              <Button onClick={handleReturnToReviews} variant="secondary">
                ✏️ Rəylərə Bax və Düzəliş Et
              </Button>
              )}
              <Button onClick={handleBack}>
                ← Əsas Səhifəyə Qayıt
              </Button>
            </div>
          </CompletionMessage>
        </Content>
      </Container>
    );
  }

  // Boş state yalnız yükləmə tam bitəndən sonra (loading false) və məlumat həqiqətən yoxdursa
  if (!loading && !assignment) {
    return (
      <Container>
        <EmptyState>
          <EmptyStateText>Yoxlama məlumatları tapılmadı</EmptyStateText>
        </EmptyState>
      </Container>
    );
  }

  if (!loading && !isApellyasiyaMode && (!students || students.length === 0)) {
    return (
      <Container>
        <EmptyState>
          <EmptyStateText>Yoxlama məlumatları tapılmadı</EmptyStateText>
        </EmptyState>
      </Container>
    );
  }

  if (!loading && isApellyasiyaMode && (!students || students.length === 0)) {
    return (
      <Container>
        <EmptyState>
          <EmptyStateText>Apellyasiya üçün şagird məlumatı tapılmadı</EmptyStateText>
        </EmptyState>
      </Container>
    );
  }

  if (!assignment) return null;

  return (
    <Container>
      <Header onClick={() => isHeaderMenuOpen && setIsHeaderMenuOpen(false)}>
        <HeaderLeft>

          <Title>Buraxılış Yoxlaması</Title>
          <Subtitle>Şagird cavab kağızlarını sual-sual qiymətləndirin</Subtitle>
          <MetaRow>
            <MetaTag>Fənn: {assignment.fenn}</MetaTag>
            <MetaTag>Sinif: {assignment.sinif}</MetaTag>
       
                      <QuestionBadge>
            <span>{questionNumberDisplay ? `Sual ${questionNumberDisplay}` : questionNumber}</span>
          </QuestionBadge>
            {isApellyasiyaMode && (
              <MetaTag style={{
                background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                color: '#92400e',
                border: '2px solid #f59e0b'
              }}>
                📝 Apellyasiya
              </MetaTag>
            )}
            {isPoolAssignment && (
              <PoolBadge>
                🏊 Hovuz
              </PoolBadge>
            )}
          </MetaRow>
          {isApellyasiyaMode && apellyasiyaData && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
              borderRadius: '12px',
              borderLeft: '4px solid #f59e0b'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#92400e', marginBottom: '8px' }}>
                Apellyasiya Səbəbi:
              </div>
              <div style={{ fontSize: '13px', color: '#78350f', lineHeight: '1.6' }}>
                {apellyasiyaData.apellyasiya_səbəbi}
              </div>
              {apellyasiyaData.əvvəlki_yoxlama && (
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px' }}>
                    <strong>Əvvəlki bal:</strong> {apellyasiyaData.əvvəlki_yoxlama.score} 
                    <span style={{ marginLeft: '12px' }}>
                      <strong>Müəllim:</strong> {apellyasiyaData.əvvəlki_yoxlama.hoca}
                    </span>
                  </div>
                </div>
              )}
              {/* Apellyasiya nəticəsi varsa göster */}
              {(() => {
                if (!currentStudent || !assignment) return null;
                const gradingFieldName = `${assignment.questionNumber}_c`;
                const gradingData: any = (currentStudent as any)[gradingFieldName];
                if (gradingData && gradingData.apellyasiya_status === true) {
                  return (
                    <div style={{ 
                      marginTop: '12px', 
                      paddingTop: '12px', 
                      borderTop: '2px solid #f59e0b',
                      background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
                      padding: '12px',
                      borderRadius: '8px',
                      borderLeft: '4px solid #10b981'
                    }}>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#166534', marginBottom: '8px' }}>
                        ✅ Apellyasiya Nəticəsi:
                      </div>
                      {gradingData.previous_score && gradingData.new_score && (
                        <div style={{ fontSize: '13px', color: '#166534', marginBottom: '6px' }}>
                          <strong>Əvvəlki bal:</strong> {gradingData.previous_score} → 
                          <strong style={{ color: gradingData.previous_score !== gradingData.new_score ? '#059669' : '#166534' }}>
                            {' '}Yeni bal: {gradingData.new_score}
                          </strong>
                          {gradingData.previous_score !== gradingData.new_score && (
                            <span style={{ marginLeft: '8px', color: '#059669', fontWeight: '600' }}>
                              (Dəyişdi)
                            </span>
                          )}
                        </div>
                      )}
                      {gradingData.apellyasiya_note && (
                        <div style={{ fontSize: '13px', color: '#166534', marginBottom: '6px' }}>
                          <strong>Müəllim qeydi:</strong> {gradingData.apellyasiya_note}
                        </div>
                      )}
                      {gradingData.apellyasiya_hoca && gradingData.apellyasiya_hocaId !== gradingData.hocaId && (
                        <div style={{ fontSize: '13px', color: '#166534', marginBottom: '6px' }}>
                          <strong>Apellyasiyanı yoxlayan:</strong> {gradingData.apellyasiya_hoca}
                        </div>
                      )}
                      {gradingData.apellyasiya_onaylanma_tarihi && (
                        <div style={{ fontSize: '12px', color: '#166534', marginTop: '6px' }}>
                          Təsdiqlənmə tarixi: {new Date(
                            gradingData.apellyasiya_onaylanma_tarihi.toDate ? 
                            gradingData.apellyasiya_onaylanma_tarihi.toDate() : 
                            gradingData.apellyasiya_onaylanma_tarihi
                          ).toLocaleString('az-AZ')}
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </HeaderLeft>

        {/* Header actions with students button */}
        <HeaderActions>
          <StudentsButton
            onClick={() => setIsMobileDrawerOpen(true)}
            aria-label="Şagird siyahısını aç"
          >
            Şagirdlər
            <span className="count">{listFilteredStudents.length}</span>
          </StudentsButton>

     <ViewQuestionButton onClick={handleOpenQuestionModal}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
              Suala bax
            </ViewQuestionButton>

          <HeaderMenuButton onClick={() => setIsHeaderMenuOpen(prev => !prev)}>
            ⋯
          </HeaderMenuButton>

          {/* Header Menu */}
          {isHeaderMenuOpen && (
            <HeaderMenu onClick={(e) => e.stopPropagation()}>
              <HeaderMenuItem
                onClick={() => {
                  setIsHeaderMenuOpen(false);
                  setShowSettingsModal(true);
                }}
              >
                <span style={{ fontSize: '16px' }}>⚙️</span>
                <span>Tənzimləmələr</span>
              </HeaderMenuItem>
              <HeaderMenuItem
                onClick={() => {
                  setIsHeaderMenuOpen(false);
                  handleBack();
                }}
              >
                <span style={{ fontSize: '16px' }}>←</span>
                <span>Tapşırıqlara qayıt</span>
              </HeaderMenuItem>
            </HeaderMenu>
          )}
        </HeaderActions>
      </Header>

      <Content>
        <ProgressBar $isCompleted={isCompleted}>
          <ProgressText $isCompleted={isCompleted}>
            {gradedCount} / {effectiveTotal} şagird qiymətləndirildi
            {` (${Math.round(progress)}%)`}
          </ProgressText>
          <ProgressBarOuter>
            <ProgressBarInner width={progress} />
          </ProgressBarOuter>
        </ProgressBar>

        {/* Lock warning for pool assignments */}
        {lockWarning && isPoolAssignment && (
          <LockWarningBanner>
            <span className="icon">⚠️</span>
            <span className="text">{lockWarning}</span>
          </LockWarningBanner>
        )}

        {currentStudent && (
          <MainLayout>
            {/* Left Column - Student Info & Image */}
            <LeftColumn>
              <StudentGradingCard>
                <StudentInfo>
                  <StudentName>
                    {maskName(currentStudent.firstName, currentStudent.lastName)}
                    {gradedStudents.has(currentStudent.student_id) && (
                      <span style={{ 
                        marginLeft: '12px', 
                        color: '#10b981',
                        fontSize: '20px'
                      }}>
                        ✓
                      </span>
                    )}
                  </StudentName>
                  <StudentDetails>
                    {!isApellyasiyaMode && (
                    <span><strong>İştriakçı No:</strong> {maskStudentId(currentStudent.student_id)}</span>
                    )}
                    <span><strong>Sinif:</strong> {currentStudent.grade}</span>
                    {currentStudent.variant && (
                      <span><strong>Variant:</strong> {currentStudent.variant}</span>
                    )}
                    <span><strong>Sıra:</strong> {currentIndex + 1} / {filteredStudents.length}</span>
                  </StudentDetails>
                </StudentInfo>
              </StudentGradingCard>

              <ImageSection>
                {imageUrl ? (
                  <>
                    <ZoomControlsArea>
                      <QuestionNumberLabel>
                        {questionNumberDisplay ? `Sual ${questionNumberDisplay}` : questionNumber}
                      </QuestionNumberLabel>
                      <ZoomButton onClick={handleZoomIn}>+</ZoomButton>
                      <ZoomButton onClick={handleZoomOut}>-</ZoomButton>
                      <ZoomButton onClick={handleResetZoom}>Reset</ZoomButton>
                      {isZoomed && !imageLoading && (
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#64748b',
                          marginLeft: '12px'
                        }}>
                          Zoom: {Math.round(zoomLevel * 100)}%
                        </div>
                      )}
                    </ZoomControlsArea>

                    <QuestionImage style={{ 
                      position: 'relative',
                      overflow: 'auto',
                      cursor: isZoomed ? 'grab' : 'zoom-in'
                    }}>
                      {imageLoading && (
                        <LoadingContainer>
                          <LoadingSpinner />
                          <LoadingText>Şəkil yüklənir...</LoadingText>
                        </LoadingContainer>
                      )}
                      <img 
                        src={imageUrl} 
                        alt={`${questionNumber}`}
                        style={{
                          width: isZoomed ? `${100 * zoomLevel}%` : '100%',
                          height: 'auto',
                          objectFit: 'contain',
                          display: imageLoading ? 'none' : 'block',
                          cursor: 'zoom-in'
                        }}
                        onLoad={() => { setImageLoading(false); setIsStudentLoading(false); }}
                        onError={() => { setImageLoading(false); setIsStudentLoading(false); }}
                        onClick={() => setIsImageModalOpen(true)}
                      />
                    </QuestionImage>
                  </>
                ) : (
                  <div style={{ 
                    padding: '40px', 
                    background: '#f8fafc', 
                    borderRadius: '12px',
                    textAlign: 'center',
                    color: '#64748b'
                  }}>
                    Bu sual üçün şəkil tapılmadı ({questionNumberDisplay ? `Sual ${questionNumberDisplay}` : questionNumber})
                  </div>
                )}
              </ImageSection>
            </LeftColumn>

            {/* Right Column - Grading & Students List */}
            <RightColumn>
              <GradingSection>
                {/* Simplified header: fənn və sual nömrəsi only - duplicate student info removed */}
                <GradingHeader>
                  {assignment.fenn} Qiymətləndirməsi
                  {questionNumberDisplay && (
                    <>
                      {' - '}
                      <GradingQuestionLabel>Sual {questionNumberDisplay}</GradingQuestionLabel>
                    </>
                  )}
                </GradingHeader>

                {isStudentLoading ? (
                  <LoadingContainer>
                    <LoadingSpinner />
                    <LoadingText>Şagird məlumatları yüklənir...</LoadingText>
                  </LoadingContainer>
                ) : (
                  <>
                    {isQ26SpecialGrading(assignment, questionNumber) ? (
                      // Q26 üçün özel meyar UI-i
                      <>
                        {/* Eski normal modda verilmiş yoklama varsa preview göster */}
                        {(() => {
                          const gradingFieldName = `${questionNumber}_c`;
                          const gradingData: any = (currentStudent as any)[gradingFieldName];
                          const hasOldGrading = gradingData && 
                            typeof gradingData === 'object' && 
                            gradingData.score && 
                            !gradingData.criteria &&
                            gradingData.hocaId === teacher.id;
                          
                          if (hasOldGrading) {
                            return (
                              <div style={{
                                marginBottom: '20px',
                                padding: '16px',
                                background: '#fef3c7',
                                border: '2px solid #f59e0b',
                                borderRadius: '10px',
                                borderLeft: '4px solid #f59e0b'
                              }}>
                                <div style={{
                                  fontSize: '14px',
                                  fontWeight: '700',
                                  color: '#92400e',
                                  marginBottom: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}>
                                  ⚠️ Əvvəlki Yoxlama (Normal Mod)
                                </div>
                                <div style={{
                                  fontSize: '16px',
                                  fontWeight: '600',
                                  color: '#78350f',
                                  marginBottom: '4px'
                                }}>
                                  Bal: {gradingData.score}
                                </div>
                                {gradingData.note && (
                                  <div style={{
                                    fontSize: '14px',
                                    color: '#92400e',
                                    marginTop: '8px',
                                    paddingTop: '8px',
                                    borderTop: '1px solid #fbbf24'
                                  }}>
                                    <strong>Qeyd:</strong> {gradingData.note}
                                  </div>
                                )}
                                <div style={{
                                  fontSize: '12px',
                                  color: '#a16207',
                                  marginTop: '8px',
                                  fontStyle: 'italic'
                                }}>
                                  Yuxarıda yeni Q26 meyarları ilə yenidən qiymətləndirə bilərsiniz
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        
                        <CriteriaSection>
                          <CriteriaTitle>
                            Məzmun/Tapşırığın Yerinə Yetirilməsi
                            <CriteriaMaxScore>Max: 2</CriteriaMaxScore>
                            <InfoIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenCriteriaModal('content');
                              }}
                              title="Meyar açıqlamaları"
                            >
                              i
                            </InfoIcon>
                          </CriteriaTitle>
                          <CriteriaOptions>
                            {[0, 1, 2].map((score) => (
                              <CriteriaButton
                                key={score}
                                selected={contentScore === score}
                                onClick={() => {
                                  setContentScore(contentScore === score ? null : score);
                                }}
                              >
                                {score}
                              </CriteriaButton>
                            ))}
                          </CriteriaOptions>
                        </CriteriaSection>

                        <CriteriaSection>
                          <CriteriaTitle>
                            Ardıcıllıq/Məntiqi Rabitə
                            <CriteriaMaxScore>Max: 1</CriteriaMaxScore>
                            <InfoIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenCriteriaModal('sequence');
                              }}
                              title="Meyar açıqlamaları"
                            >
                              i
                            </InfoIcon>
                          </CriteriaTitle>
                          <CriteriaOptions>
                            {[0, 0.5, 1].map((score) => (
                              <CriteriaButton
                                key={score}
                                selected={sequenceScore === score}
                                onClick={() => {
                                  setSequenceScore(sequenceScore === score ? null : score);
                                }}
                              >
                                {score}
                              </CriteriaButton>
                            ))}
                          </CriteriaOptions>
                        </CriteriaSection>

                        <CriteriaSection>
                          <CriteriaTitle>
                            Qrammatika
                            <CriteriaMaxScore>Max: 1</CriteriaMaxScore>
                            <InfoIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenCriteriaModal('grammar');
                              }}
                              title="Meyar açıqlamaları"
                            >
                              i
                            </InfoIcon>
                          </CriteriaTitle>
                          <CriteriaOptions>
                            {[0, 0.5, 1].map((score) => (
                              <CriteriaButton
                                key={score}
                                selected={grammarScore === score}
                                onClick={() => {
                                  setGrammarScore(grammarScore === score ? null : score);
                                }}
                              >
                                {score}
                              </CriteriaButton>
                            ))}
                          </CriteriaOptions>
                        </CriteriaSection>

                        <CriteriaSection>
                          <CriteriaTitle>
                            Söz Ehtiyatı
                            <CriteriaMaxScore>Max: 1</CriteriaMaxScore>
                            <InfoIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenCriteriaModal('vocabulary');
                              }}
                              title="Meyar açıqlamaları"
                            >
                              i
                            </InfoIcon>
                          </CriteriaTitle>
                          <CriteriaOptions>
                            {[0, 0.5, 1].map((score) => (
                              <CriteriaButton
                                key={score}
                                selected={vocabularyScore === score}
                                onClick={() => {
                                  setVocabularyScore(vocabularyScore === score ? null : score);
                                }}
                              >
                                {score}
                              </CriteriaButton>
                            ))}
                          </CriteriaOptions>
                        </CriteriaSection>

                        <CriteriaSection>
                          <CriteriaTitle>
                            Çıxılan Bal/Cərimə
                            <CriteriaMaxScore>Max: 1</CriteriaMaxScore>
                            <InfoIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenCriteriaModal('penalty');
                              }}
                              title="Meyar açıqlamaları"
                            >
                              i
                            </InfoIcon>
                          </CriteriaTitle>
                          <CriteriaOptions>
                            {[0, 0.5, 1].map((score) => (
                              <CriteriaButton
                                key={score}
                                selected={penaltyScore === score}
                                onClick={() => {
                                  setPenaltyScore(penaltyScore === score ? null : score);
                                }}
                              >
                                {score}
                              </CriteriaButton>
                            ))}
                          </CriteriaOptions>
                        </CriteriaSection>

                        <TotalDisplay>
                          <TotalLabel>Ümumi Bal</TotalLabel>
                          <TotalValue>{calculateQ26Total().toFixed(1)} / 5</TotalValue>
                        </TotalDisplay>

                        <ScoreInput>
                          <ScoreLabel htmlFor="note">Qeyd (ixtiyari)</ScoreLabel>
                          <NoteInput
                            id="note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Əlavə qeydlər..."
                          />
                        </ScoreInput>
                      </>
                    ) : (
                      // Normal sorular üçün: mevcut UI
                      <>
                        <div>
                          <ScoreLabel>Bal</ScoreLabel>
                          <ScoreOptions>
                            {scoreOptions.map((option) => (
                              <ScoreButton
                                key={option.value}
                                selected={selectedScore === option.value}
                                onClick={() => {
                                  if (selectedScore === option.value) {
                                    setSelectedScore(null);
                                  } else {
                                    setSelectedScore(option.value);
                                  }
                                }}
                              >
                                {option.label}
                              </ScoreButton>
                            ))}
                          </ScoreOptions>
                        </div>

                        <ScoreInput>
                          <ScoreLabel htmlFor="note">Qeyd (ixtiyari)</ScoreLabel>
                          <NoteInput
                            id="note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Əlavə qeydlər..."
                          />
                        </ScoreInput>
                      </>
                    )}

                    {isApellyasiyaMode && (
                      <ScoreInput style={{ marginTop: '16px' }}>
                        <ScoreLabel htmlFor="apellyasiyaNote" style={{ color: '#f59e0b', fontWeight: '700' }}>
                          📝 Apellyasiya Nəticəsi Qeydi (VACİB) *
                        </ScoreLabel>
                        <NoteInput
                          id="apellyasiyaNote"
                          value={apellyasiyaNote}
                          onChange={(e) => setApellyasiyaNote(e.target.value)}
                          placeholder="Apellyasiya nəticəsini yazın..."
                          style={{
                            border: '2px solid #f59e0b',
                            background: '#fffbeb',
                            minHeight: '120px'
                          }}
                        />
                      </ScoreInput>
                    )}

                    {isCurrentStudentGraded && (
                      <div style={{
                        width: '100%',
                        marginBottom: '8px',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        background: '#dcfce7',
                        color: '#166534',
                        fontSize: '13px',
                        fontWeight: 600,
                        textAlign: 'center'
                      }}>
                        ✅ Dəyərləndirmə təsdiqləndi
                      </div>
                    )}

                    {/* Şagird naviqasiyası - YoxlamaPage-dəki mobil nav-a bənzər info */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      marginTop: '4px'
                    }}>
                      <div style={{
                        padding: '6px 14px',
                        background: '#f1f5f9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#475569',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span>{currentIndex + 1} / {filteredStudents.length}</span>
                        {gradedStudents.has(currentStudent.student_id) && <span>✅</span>}
                      </div>
                    </div>

                    {/* Desktop ButtonGroup - visible on desktop/tablet */}
                    <ButtonGroup>
                      <Button
                        variant="secondary"
                        onClick={handlePrevious}
                        disabled={currentIndex === 0}
                        style={{ minWidth: 0 }}
                        aria-label="Əvvəlki şagirdə keç"
                      >
                        ← Əvvəlki
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleSkip}
                        disabled={currentIndex === filteredStudents.length - 1 || !isCurrentStudentGraded}
                        style={{ minWidth: 0 }}
                        title={
                          !isCurrentStudentGraded
                            ? 'Növbəti şagirdə keçmək üçün əvvəlcə bu şagirdi qiymətləndirin'
                            : 'Növbəti şagirdə keç'
                        }
                        aria-label="Növbəti şagirdə keç"
                      >
                        Növbəti →
                      </Button>
                      <Button
                        onClick={handleSaveAndNext}
                        disabled={
                          saving ||
                          (isApellyasiyaMode && !apellyasiyaNote.trim()) ||
                          (isQ26SpecialGrading(assignment, questionNumber)
                            ? (contentScore === null || sequenceScore === null || grammarScore === null || vocabularyScore === null)
                            : !selectedScore)
                        }
                        style={isApellyasiyaMode ? {
                          minWidth: 0,
                          background: 'linear-gradient(135deg, #f59e0b, #d97706)'
                        } : { minWidth: 0 }}
                        data-confirm-button="true"
                        aria-label={
                          isApellyasiyaMode
                            ? 'Apellyasiyanı təsdiqlə'
                            : isCurrentStudentGraded
                              ? 'Düzəlişləri təsdiqlə və növbəti şagirdə keç'
                              : 'Qiymətləndirməni təsdiqlə və növbəti şagirdə keç'
                        }
                      >
                        {saving
                          ? '⏳ Qeyd edilir...'
                          : isApellyasiyaMode
                            ? '✅ Apellyasiyanı Təsdiqlə'
                          : isCurrentStudentGraded
                            ? '💾 Düzəlişləri Təstiq Et'
                            : 'Dəyərləndirməni Təsdiq Et'}
                      </Button>
                    </ButtonGroup>
                    {enterKeyEnabled && !isApellyasiyaMode && (
                      <div style={{
                        marginTop: '8px',
                        textAlign: 'center',
                        fontSize: '13px',
                        color: '#64748b',
                        fontWeight: '500'
                      }}>
                        ⏎ Enter tuşu ilə təsdiqlə
                      </div>
                    )}

                    {/* NEW: Mobile Sticky Footer - thumb-friendly buttons for mobile */}
                    <MobileStickyFooter>
                      <MobilePrimaryButton
                        onClick={handleSaveAndNext}
                        $disabled={
                          saving ||
                          (isApellyasiyaMode && !apellyasiyaNote.trim()) ||
                          (isQ26SpecialGrading(assignment, questionNumber)
                            ? (contentScore === null || sequenceScore === null || grammarScore === null || vocabularyScore === null)
                            : !selectedScore)
                        }
                        data-confirm-button="true"
                      >
                        {saving
                          ? '⏳ Qeyd edilir...'
                          : isApellyasiyaMode
                            ? '✅ Apellyasiyanı Təsdiqlə'
                          : isCurrentStudentGraded
                            ? '💾 Düzəlişləri Təstiq Et'
                            : 'Dəyərləndirməni Təsdiq Et'}
                      </MobilePrimaryButton>

                      <MobileSecondaryButtons>
                        <MobileNavButton
                          onClick={handlePrevious}
                          $disabled={currentIndex === 0}
                          aria-label="Əvvəlki şagirdə keç"
                        >
                          ← Əvvəlki
                        </MobileNavButton>

                        <MobileNavButton
                          onClick={() => {
                            if (currentIndex < filteredStudents.length - 1 && isCurrentStudentGraded) {
                              setCurrentIndex(currentIndex + 1);
                            }
                          }}
                          $disabled={currentIndex >= filteredStudents.length - 1 || !isCurrentStudentGraded}
                          aria-label="Növbəti şagirdə keç"
                          title={
                            !isCurrentStudentGraded
                              ? 'Növbəti şagirdə keçmək üçün əvvəlcə bu şagirdi qiymətləndirin'
                              : 'Növbəti şagirdə keç'
                          }
                        >
                          Növbəti →
                        </MobileNavButton>
                      </MobileSecondaryButtons>

                      {enterKeyEnabled && !isApellyasiyaMode && (
                        <div style={{
                          textAlign: 'center',
                          fontSize: '12px',
                          color: '#64748b',
                          fontWeight: '500',
                          marginTop: '8px'
                        }}>
                          ⏎ Enter tuşu ilə təsdiqlə
                        </div>
                      )}
                    </MobileStickyFooter>
                  </>
                )}
              </GradingSection>

              <StudentsList>
                <StudentsTitle>Şagirdlər ({listFilteredStudents.length})</StudentsTitle>
                <StudentsGrid>
                  {listFilteredStudents.map((student, index) => {
                    // Get original index in filteredStudents (global data)
                    const originalIndex = filteredStudents.findIndex(s => s.student_id === student.student_id);
                    const score = getStudentScore(student, assignment.questionNumber);
                    return (
                      <StudentCard
                        key={student.student_id}
                        student={student}
                        isActive={originalIndex === currentIndex}
                        isCompleted={gradedStudents.has(student.student_id)}
                        isApellyasiyaMode={isApellyasiyaMode}
                        studentNumber={index + 1}
                        score={score}
                        questionNumber={assignment.questionNumber}
                        assignment={assignment}
                        onClick={() => {
                          setCurrentIndex(originalIndex);
                          const isQ26 = isQ26SpecialGrading(assignment, questionNumber);
                          if (isQ26) {
                            setContentScore(null);
                            setSequenceScore(null);
                            setGrammarScore(null);
                            setVocabularyScore(null);
                            setPenaltyScore(null);
                          }
                          setSelectedScore(null);
                          setNote('');
                          setImageLoading(true);
                          handleResetZoom();
                        }}
                      />
                    );
                  })}
                </StudentsGrid>
              </StudentsList>
            </RightColumn>
          </MainLayout>
        )}

        {/* Image Modal - Full screen, auto-rotates 90deg on mobile */}
        <ImageModal $isOpen={isImageModalOpen} onClick={() => setIsImageModalOpen(false)}>
          <CloseButton onClick={(e) => { e.stopPropagation(); setIsImageModalOpen(false); }}>
            ×
          </CloseButton>
          <ModalImageContainer>
            {imageUrl && (
              <ModalImage
                src={imageUrl}
                alt={questionNumberDisplay ? `Sual ${questionNumberDisplay}` : `${questionNumber}`}
              />
            )}
          </ModalImageContainer>
        </ImageModal>

        {/* Criteria Info Modal */}
        {openCriteriaModal && q26CriteriaData[openCriteriaModal as keyof typeof q26CriteriaData] && (
          <CriteriaModal
            $isOpen={!!openCriteriaModal}
            onClick={() => setOpenCriteriaModal(null)}
          >
            <CriteriaModalContent onClick={(e) => e.stopPropagation()}>
              <CriteriaModalHeader>
                <CriteriaModalTitle>
                  {q26CriteriaData[openCriteriaModal as keyof typeof q26CriteriaData].title}
                </CriteriaModalTitle>
                <CriteriaModalClose onClick={() => setOpenCriteriaModal(null)}>
                  ×
                </CriteriaModalClose>
              </CriteriaModalHeader>
              <CriteriaModalBody>
                {q26CriteriaData[openCriteriaModal as keyof typeof q26CriteriaData].ranges.map((range, index) => (
                  <ScoreRangeSection key={index}>
                    <ScoreRangeTitle>
                      <ScoreRangeValue>{range.score} bal</ScoreRangeValue>
                    </ScoreRangeTitle>
                    <ScoreRangeDescription>
                      {range.description.map((desc, descIndex) => (
                        <ScoreRangeItem key={descIndex}>{desc}</ScoreRangeItem>
                      ))}
                    </ScoreRangeDescription>
                  </ScoreRangeSection>
                ))}
              </CriteriaModalBody>
            </CriteriaModalContent>
          </CriteriaModal>
        )}

        <TeacherSettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          onSettingChange={() => setEnterKeyEnabled(getEnterKeyShortcutPreference())}
        />

        <KeyboardShortcutsModal
          isOpen={showShortcutsModal}
          onClose={() => setShowShortcutsModal(false)}
          gradingMode={gradingMode}
        />

        {/* Question Image Modal */}
        <QuestionImageModal
          $isOpen={isQuestionModalOpen}
          onClick={handleCloseQuestionModal}
        >
          <QuestionModalCloseButton onClick={handleCloseQuestionModal}>
            ×
          </QuestionModalCloseButton>
          <QuestionModalContent onClick={(e) => e.stopPropagation()}>
            {questionImageLoading ? (
              <QuestionModalErrorMessage>
                <LoadingSpinner />
                <QuestionModalErrorText>Şəkil yüklənir...</QuestionModalErrorText>
              </QuestionModalErrorMessage>
            ) : questionImageError ? (
              <QuestionModalErrorMessage>
                <QuestionModalErrorIcon>⚠️</QuestionModalErrorIcon>
                <QuestionModalErrorText>
                  Şəkil yüklənməyib
                  <br />
                  Administratorla əlaqə saxlayın
                </QuestionModalErrorText>
              </QuestionModalErrorMessage>
            ) : (
              <QuestionModalImage
                src={questionImageUrl || ''}
                alt={`Sual ${questionNumberDisplay || questionNumber}`}
              />
            )}
          </QuestionModalContent>
        </QuestionImageModal>

        {/* Mobile Students List Drawer - Only visible on mobile/tablet */}
        {/* REMOVED: MobileStudentsTrigger FAB - now using StudentsButton in header */}

        <MobileDrawer $isOpen={isMobileDrawerOpen} onClick={() => setIsMobileDrawerOpen(false)}>
          <DrawerContent onClick={(e) => e.stopPropagation()}>
            <DrawerHandle />

            {/* NEW: Filter tabs in mobile drawer */}
            <FilterTabs>
              <FilterTab
                $active={listFilterType === 'all'}
                onClick={() => setListFilterType('all')}
                aria-label="Bütün şagirdləri göstər"
                aria-pressed={listFilterType === 'all'}
              >
                Bütün şagirdlər
              </FilterTab>
              <FilterTab
                $active={listFilterType === 'ungraded'}
                onClick={() => setListFilterType('ungraded')}
                aria-label="Yoxlanılmamış şagirdləri göstər"
                aria-pressed={listFilterType === 'ungraded'}
              >
                Yoxlanılmamışlar
              </FilterTab>
              <FilterTab
                $active={listFilterType === 'completed'}
                onClick={() => setListFilterType('completed')}
                aria-label="Tamamlanmış şagirdləri göstər"
                aria-pressed={listFilterType === 'completed'}
              >
                Tamamlanmışlar
              </FilterTab>
            </FilterTabs>

            {/* NEW: Search input in mobile drawer */}
        

            <StudentsTitle>Şagirdlər ({listFilteredStudents.length})</StudentsTitle>
            <StudentsGrid>
              {listFilteredStudents.map((student, index) => {
                // Get original index in filteredStudents (global data)
                const originalIndex = filteredStudents.findIndex(s => s.student_id === student.student_id);
                const score = getStudentScore(student, assignment.questionNumber);
                return (
                  <StudentCard
                    key={student.student_id}
                    student={student}
                    isActive={originalIndex === currentIndex}
                    isCompleted={gradedStudents.has(student.student_id)}
                    isApellyasiyaMode={isApellyasiyaMode}
                    studentNumber={index + 1}
                    score={score}
                    questionNumber={assignment.questionNumber}
                    assignment={assignment}
                    onClick={() => {
                      setCurrentIndex(originalIndex);
                      setIsMobileDrawerOpen(false);
                      const isQ26 = isQ26SpecialGrading(assignment, questionNumber);
                      if (isQ26) {
                        setContentScore(null);
                        setSequenceScore(null);
                        setGrammarScore(null);
                        setVocabularyScore(null);
                        setPenaltyScore(null);
                      }
                      setSelectedScore(null);
                      setNote('');
                      setImageLoading(true);
                      handleResetZoom();
                    }}
                  />
                );
              })}
            </StudentsGrid>
          </DrawerContent>
        </MobileDrawer>
      </Content>
    </Container>
  );
};

export default BuraxilisYoxlamaPage;
